/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react';
import { func, string } from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'ui-component-library';

import {
  PeekContainer,
  TopContainer,
  DetailLink,
  IconContainer,
  CardContainer,
  LoadingContainer,
  HeaderContent,
  LockIcon,
  LoadNotFoundContainer,
  LoadNotFoundTitle,
  LoadNotFoundMessage,
} from './tracking-queue-peek-load.style';
import TrackingActionsCard from '../../core/common/action-card-tracking-actions';
import ProblemsWithLoad from '../../core/common/problems-with-load';
import icon from '../../../assets/icon-dismissal-x.svg';
import lockIcon from '../../../assets/icon-lock.svg';
import TransitTracker from '../../core/tl/card-transit-tracker';
import DriverTruckInfo from '../../core/tl/card-driver-truck-info/card-driver-truck-info';
import ShipmentStops from '../../core/common/card-shipment-stops';
import LastHumanUpdate from '../../core/tl/card-last-human-update';
import LoadIdentifiersCard from '../../core/ltl/load-identifiers-card';
import useLockLoad from '../../../hooks/load-lock';
import LockModal from '../../core/common/modals/lock-modal';
import { fetchLoadSummary } from '../../../store/load-summary/thunkActions';
import { clearLoadSummary } from '../../../store/load-summary/slice';
import Tooltip from '../../core/common/tooltip';
import { clearSelectedLoad } from '../../../store/selected-load/slice';
import { LOCK_EXPIRATION_STATES, MODE_TYPES, STATUS_TYPES } from '../../../constants';
import { formatUsername } from '../../../utils/common';
import ImageIcon from '../../core/common/image-icon';

/**
 * Peek Load Component
 * @param {object} params - Object containing component params
 * @param {object} params.loadId - ID of the load
 * @param {object} params.billOfLadingNumber - bill of Lading Number
 * @param {object} params.salesOrderBk - Sales order bussiness key
 * @param {string} params.mode - Mode type
 * @param {func} params.closePeek - Function to handle closing peek view.
 * @return {element}
 */
const TrackingQueuePeekLoad = ({
  loadId, salesOrderBk, closePeek, mode,
}) => {
  // State hook
  const [showLockExpiringModal, setShowLockExpiringModal] = useState(false);

  // General Hook
  const location = useLocation();

  // Redux hooks
  const dispatch = useDispatch();
  const loadSummary = useSelector((state) => state.loadSummary.data);
  const loadSummaryJob = useSelector((state) => state.loadSummary);

  // Data fetching effect
  useEffect(() => {
    dispatch(fetchLoadSummary(loadId, salesOrderBk));
    return () => {
      dispatch(clearLoadSummary());
    };
  }, [dispatch, loadId, salesOrderBk]);

  /**
    * Refreshes the load summary data in the peek view.
    * @param {object} overrideData - Object containing load properties to override on the refreshed data.
    * Used for properties in the index that don't update synchronously.
    * @return {void} - This function does not return anything.
    */
  const refetchData = (overrideData) => {
    dispatch(fetchLoadSummary(loadId, salesOrderBk, overrideData));
    setShowLockExpiringModal(false);
  };

  // Load lock hook
  const loadLock = useLockLoad(loadSummary, refetchData, () => setShowLockExpiringModal(true));

  /**
  * Helper function to close the peek view
  * intermittently make sure locks are cleared.
  * @return {void}
  */
  const closePeekView = () => {
    if (loadLock && loadLock.ownedByUser) {
      loadLock.clearLock();
    }
    if (loadLock) {
      loadLock.clearTimeouts();
    }
    closePeek();
  };

  /**
   * Closes the lock expiration modal.
   * If the expiring state of the lack at the given moment is expiring,
   * call the locks acknowledgeExpiration method to acknoledge that the lock
   * is expiring.
   * If the lock is expired, clear the loadSummary and selectedLoad
   * and ensure no timeouts are set on the current lock
   * to cause the peek view to close upstream.
   * @return {void}
   */
  const closeLockExpirationModal = () => {
    setShowLockExpiringModal(false);
    if (loadLock.expiring === LOCK_EXPIRATION_STATES.expiring) {
      loadLock.acknowledgeExpiration();
    } else if (loadLock.expiring === LOCK_EXPIRATION_STATES.expired) {
      dispatch(clearSelectedLoad());
      dispatch(clearLoadSummary());
      loadLock.clearTimeouts();
    }
  };

  if (loadSummaryJob.status === STATUS_TYPES.FAILED_STATE && loadSummaryJob.error.status === 404) {
    return (
      <LoadNotFoundContainer>
        <LoadNotFoundTitle>Whoops... load not found!</LoadNotFoundTitle>
        <LoadNotFoundMessage>
          This load can no longer be found in Track & Trace due to its current status.
        </LoadNotFoundMessage>
      </LoadNotFoundContainer>
    );
  }

  /**
   * Renders the lock state for the tracking queue peek view
   * header.
   */
  const renderLockState = () => {
    const content = `This load is currently locked by ${formatUsername(loadLock.initiatorName)}.`;
    return (
      <Tooltip content={content} position="below" delay={0.2}>
        <LockIcon src={lockIcon} height="18px" width="18px" alt="lock" />
      </Tooltip>
    );
  };

  /**
   * Render the lock expiratio modal
   * @param {object} lock - Lock Object
   */
  const renderLockExpirationModal = (lock, summary) => {
    // If the lock is in expiring state, show the expiring modal
    const expired = lock.expiring === LOCK_EXPIRATION_STATES.expired;
    return (
      <LockModal
        showModal={showLockExpiringModal}
        closeModal={closeLockExpirationModal}
        resetTimer={() => loadLock.extendLock(refetchData)}
        loadInfo={{
          ...summary,
          loadLock: lock,
        }}
        timerExpired={expired}
        lock={loadLock}
      />
    );
  };

  // Helper const to check if a locked by another user.
  const locked = loadLock.loadId && !loadLock.ownedByUser;
  return (
    <PeekContainer data-testid="tracking-queue-peek-load">
      {(loadSummary && loadLock.loadId) && renderLockExpirationModal(loadLock, loadSummary)}
      <TopContainer locked={loadSummary && locked}>
        <DetailLink
          onClick={loadLock ? () => loadLock.clearTimeouts() : undefined}
          to={{ pathname: `/loads/detail/${loadId}`, search: `?modeType=${mode}`, state: { prevPath: location.pathname, salesOrderBk } }}
        >
          See Load Details
        </DetailLink>
        <HeaderContent>
          {(loadSummary && locked) && renderLockState(loadLock)}
          <p>
            Load ID#:
            <strong>{`  ${salesOrderBk}`}</strong>
          </p>
        </HeaderContent>
        <IconContainer>
          <ImageIcon src={icon} width={15} height={15} altString="detail icon" onClick={closePeekView} />
        </IconContainer>
      </TopContainer>
      {loadSummary && (
        <CardContainer>
          {loadSummary.problems?.length > 0
            && <ProblemsWithLoad loadProblems={loadSummary.problems} />}
          <TransitTracker
            load={loadSummary}
            lastTrackingEvent={loadSummary.lastTrackingEvent}
            refetchLoad={refetchData}
          />
          <LastHumanUpdate lastUpdate={loadSummary.lastHumanEvent} />
          <TrackingActionsCard refetchLoad={refetchData} load={loadSummary} lock={loadLock} mode={mode} />
          {mode === MODE_TYPES.lessThanTruckload && (
            <LoadIdentifiersCard
              lock={loadLock}
              load={loadSummary}
              refetchLoad={refetchData}
            />
          )}
          {mode === MODE_TYPES.truckload && (
            <DriverTruckInfo
              load={loadSummary}
              lock={loadLock}
              refetchLoad={refetchData}
            />
          )}
          <ShipmentStops
            data={loadSummary.shipmentInformation}
            loadId={loadSummary.loadId}
            status={mode === MODE_TYPES.truckload ? loadSummary.tlStatus : loadSummary.ltlStatus}
            salesOrderBk={loadSummary.billOfLadingNumber}
            refetchData={refetchData}
            mode={mode}
          />
        </CardContainer>
      )}
      {!loadSummary && (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      )}
    </PeekContainer>
  );
};

TrackingQueuePeekLoad.propTypes = {
  loadId: string.isRequired,
  salesOrderBk: string.isRequired,
  closePeek: func,
  mode: string.isRequired,
};

TrackingQueuePeekLoad.defaultProps = {
  closePeek: () => { },
};

export default TrackingQueuePeekLoad;
